import {
  getAuthenticatedAccountId,
  readStorageValue,
  removeStorageValue,
  safeJsonParse,
  sanitizeGenericText,
  sanitizeImageUrl,
  writeStorageValue,
} from '../security';
import { buildTryOnTaskImageEndpoint } from '../api';

const ACTIVE_TRY_ON_SESSION_STORAGE_KEY = 'vadauro_active_try_on_session';
const PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY = 'vadauro_pending_try_on_tasks';
const MAX_PENDING_TRY_ON_TASK_QUEUE_LENGTH = 50;
export const TRY_ON_SESSION_CHANGE_EVENT = 'vadauro:try-on-session-change';

export type TryOnImageProvider = 'standard' | 'enhanced' | 'detailed';

export interface UploadedTryOnPhotoState {
  id?: string;
  rawDataId?: string;
  rawDataS3Key?: string;
  objectKey?: string;
  isCurrentTryOnPhoto?: boolean;
  inventoryIds?: string[];
  filename: string;
  uploadedAt: string;
}

export interface TryOnSourceItemState {
  selectionId: string;
  sourceType?: 'cart' | 'product';
  cartItemId?: string;
  itemId?: string;
  inventoryIds?: string[];
  slot?: string;
  title: string;
  imageUrl: string;
  price?: number;
  accessoriesSize?: 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';
}

export interface GeneratedTryOnResultState {
  imageUrl: string;
  objectKey: string;
  resultId?: string;
  taskId?: string;
  productName: string;
  productImageUrl: string;
  price: number | null;
  provider: TryOnImageProvider;
  videoGenerationDisabled?: boolean;
  aiDataId?: string;
  isSaved?: boolean;
  itemId?: string;
  itemIds?: string[];
  inventoryIds?: string[];
  rawDataId?: string;
  sourceItems?: TryOnSourceItemState[];
}

export interface PendingTryOnTaskState {
  taskId: string;
  productName: string;
  productImageUrl: string;
  price: number | null;
  startedAt: string;
  provider: TryOnImageProvider;
  itemId?: string;
  itemIds?: string[];
  inventoryIds?: string[];
  activeInventoryIds?: string[];
  rawDataId?: string;
  sourceItems?: TryOnSourceItemState[];
  addItemSelectionId?: string;
}

export interface PendingTryOnVideoTaskState {
  taskId: string;
  sourceImageUrl: string;
  sourceImageObjectKey?: string;
  sourceImageAiDataId?: string;
  startedAt: string;
  provider: 'standard' | 'enhanced';
  model: 'motion-video' | 'standard-video' | 'portrait-video';
  prompt: string;
  itemId?: string;
  itemIds?: string[];
  inventoryIds?: string[];
  rawDataId?: string;
}

export interface GeneratedTryOnVideoResultState {
  videoUrl: string;
  videoId?: string;
  isSaved?: boolean;
  sourceVideoUrl?: string;
  sourceImageUrl: string;
  sourceImageObjectKey?: string;
  sourceImageAiDataId?: string;
  objectKey?: string;
  taskId?: string;
  provider: 'standard' | 'enhanced';
  model: 'motion-video' | 'standard-video' | 'portrait-video';
  prompt?: string;
  itemId?: string;
  itemIds?: string[];
  inventoryIds?: string[];
  rawDataId?: string;
}

interface PersistedTryOnSessionState {
  ownerKey: string;
  shouldSaveAssetsToLibrary: boolean;
  uploadedTryOnPhoto: UploadedTryOnPhotoState | null;
  generatedTryOnResult: GeneratedTryOnResultState | null;
  pendingTryOnTask: PendingTryOnTaskState | null;
  pendingTryOnVideoTask: PendingTryOnVideoTaskState | null;
  generatedTryOnVideoResult: GeneratedTryOnVideoResultState | null;
}

interface PersistedPendingTryOnTaskQueueState {
  ownerKey: string;
  tasks: PendingTryOnTaskState[];
}

function readRawPersistedTryOnSessionValue() {
  return readStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'local' })
    ?? readStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
}

function readRawPendingTryOnTaskQueueValue() {
  return readStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'local' })
    ?? readStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'session' });
}

function getTryOnSessionOwnerKey() {
  const accountId = getAuthenticatedAccountId();
  return accountId ? `account:${accountId}` : 'guest';
}

function dispatchTryOnSessionChange() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(TRY_ON_SESSION_CHANGE_EVENT));
}

function hasPersistedTryOnSession(
  value: Pick<
    PersistedTryOnSessionState,
    | 'uploadedTryOnPhoto'
    | 'generatedTryOnResult'
    | 'pendingTryOnTask'
    | 'pendingTryOnVideoTask'
    | 'generatedTryOnVideoResult'
  >
) {
  return Boolean(
    value.uploadedTryOnPhoto ||
      value.generatedTryOnResult ||
      value.pendingTryOnTask ||
      value.pendingTryOnVideoTask ||
      value.generatedTryOnVideoResult
  );
}

function getPendingTryOnTaskQueueKey(task: Pick<PendingTryOnTaskState, 'provider' | 'taskId'>) {
  return `${task.provider}:${task.taskId}`;
}

function dedupePendingTryOnTasks(tasks: PendingTryOnTaskState[]) {
  const tasksByKey = new Map<string, PendingTryOnTaskState>();

  tasks.forEach((task) => {
    tasksByKey.set(getPendingTryOnTaskQueueKey(task), task);
  });

  return Array.from(tasksByKey.values())
    .sort((left, right) => {
      const leftStartedAt = Date.parse(left.startedAt);
      const rightStartedAt = Date.parse(right.startedAt);
      return (Number.isFinite(leftStartedAt) ? leftStartedAt : 0) -
        (Number.isFinite(rightStartedAt) ? rightStartedAt : 0);
    })
    .slice(-MAX_PENDING_TRY_ON_TASK_QUEUE_LENGTH);
}

function sanitizeRemoteAssetUrl(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : '';
  } catch {
    return '';
  }
}

function sanitizeDataImageUrl(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();
  return /^data:image\/[a-zA-Z0-9.+-]+;base64,[a-zA-Z0-9+/=\s]+$/i.test(trimmedValue)
    ? trimmedValue.replace(/\s+/g, '')
    : '';
}

function sanitizeTryOnResultImageUrl(value: unknown) {
  return sanitizeImageUrl(value, true) || sanitizeDataImageUrl(value);
}

function sanitizeTryOnVideoSourceImageUrl(value: unknown) {
  return sanitizeImageUrl(value, true, true);
}

function isRemoteGeneratedMediaUrl(value: unknown) {
  if (typeof value !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(value.trim());
    return parsed.hostname.endsWith('.aliyuncs.com');
  } catch {
    return false;
  }
}

function sanitizeOptionalUuid(value: unknown) {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return /^[0-9a-fA-F-]{36}$/.test(trimmedValue) ? trimmedValue : undefined;
}

/** Shop and admin product ids (e.g. p2); not limited to UUID shape. */
function sanitizeTryOnProductItemId(value: unknown) {
  if (value === null || value === undefined) {
    return undefined;
  }

  const asString = typeof value === 'string' ? value : String(value);
  const cleaned = sanitizeGenericText(asString.trim(), 128);
  if (!cleaned || cleaned.includes('/') || cleaned.includes('\\')) {
    return undefined;
  }

  return cleaned;
}

function sanitizeTryOnProductItemIds(value: unknown) {
  const rawValue = typeof value === 'string' ? safeJsonParse<unknown>(value, value) : value;
  const rawItems = Array.isArray(rawValue)
    ? rawValue
    : rawValue === undefined || rawValue === null || rawValue === ''
      ? []
      : [rawValue];

  const itemIds = rawItems
    .map((entry) => sanitizeTryOnProductItemId(entry))
    .filter((entry): entry is string => Boolean(entry));

  return itemIds.length > 0 ? Array.from(new Set(itemIds)) : undefined;
}

function sanitizeTryOnInventoryIds(value: unknown) {
  const rawValue = typeof value === 'string' ? safeJsonParse<unknown>(value, value) : value;
  const rawItems = Array.isArray(rawValue)
    ? rawValue
    : rawValue === undefined || rawValue === null || rawValue === ''
      ? []
      : [rawValue];

  const inventoryIds = rawItems
    .map((entry) => sanitizeOptionalUuid(typeof entry === 'string' ? entry : String(entry ?? '')))
    .filter((entry): entry is string => Boolean(entry));

  return inventoryIds.length > 0 ? Array.from(new Set(inventoryIds)) : undefined;
}

function sanitizeTryOnImageProvider(value: unknown): TryOnImageProvider {
  return value === 'standard' || value === 'enhanced' || value === 'detailed' ? value : 'standard';
}

export function getShouldSaveAssetsToLibraryState(value: unknown) {
  return value !== false && value !== 'false';
}

export function getUploadedTryOnPhotoState(value: unknown): UploadedTryOnPhotoState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const id = sanitizeOptionalUuid(candidate.id);
  const rawDataId = sanitizeOptionalUuid(candidate.rawDataId ?? candidate.raw_data_id);
  const rawDataS3Key = sanitizeGenericText(String(candidate.rawDataS3Key ?? candidate.raw_data_s3_key ?? ''), 512) || undefined;
  const objectKey = sanitizeGenericText(String(candidate.objectKey ?? candidate.object_key ?? ''), 512) || undefined;
  const isCurrentTryOnPhoto =
    candidate.is_current_try_on_photo === true ||
    candidate.isCurrentTryOnPhoto === true ||
    String(candidate.is_current_try_on_photo ?? candidate.isCurrentTryOnPhoto ?? '').toLowerCase() === 'true';
  const inventoryIds = sanitizeTryOnInventoryIds(candidate.inventoryIds ?? candidate.inventory_ids);
  const filename = sanitizeGenericText(String(candidate.filename ?? ''), 120);
  const uploadedAt = sanitizeGenericText(String(candidate.uploadedAt ?? ''), 60);

  if (!filename || !uploadedAt) {
    return null;
  }

  return {
    id,
    rawDataId,
    rawDataS3Key,
    objectKey,
    isCurrentTryOnPhoto,
    inventoryIds,
    filename,
    uploadedAt,
  };
}

function sanitizeTryOnSourceItems(value: unknown): TryOnSourceItemState[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const sourceItems = value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const candidate = entry as Record<string, unknown>;
      const selectionId = sanitizeGenericText(String(candidate.selectionId ?? candidate.cartItemId ?? ''), 120);
      const sourceType: TryOnSourceItemState['sourceType'] =
        candidate.sourceType === 'product' ? 'product' : candidate.sourceType === 'cart' ? 'cart' : undefined;
      const cartItemId = sanitizeGenericText(String(candidate.cartItemId ?? ''), 120) || undefined;
      const itemId = sanitizeTryOnProductItemId(candidate.itemId ?? candidate.item_id);
      const inventoryIds = sanitizeTryOnInventoryIds(candidate.inventoryIds ?? candidate.inventory_ids);
      const title = sanitizeGenericText(String(candidate.title ?? ''), 120);
      const imageUrl = sanitizeImageUrl(String(candidate.imageUrl ?? candidate.image_url ?? ''));
      const parsedPrice = typeof candidate.price === 'number' ? candidate.price : Number(candidate.price);
      const price = Number.isFinite(parsedPrice) && parsedPrice > 0 ? Number(parsedPrice.toFixed(2)) : undefined;
      const accessoriesSizeCandidate = sanitizeGenericText(
        String(candidate.accessoriesSize ?? candidate.accessories_size ?? ''),
        8
      ).toUpperCase();
      const accessoriesSize = (
        accessoriesSizeCandidate === 'XS'
        || accessoriesSizeCandidate === 'S'
        || accessoriesSizeCandidate === 'M'
        || accessoriesSizeCandidate === 'L'
        || accessoriesSizeCandidate === 'XL'
        || accessoriesSizeCandidate === 'XXL'
      ) ? accessoriesSizeCandidate as TryOnSourceItemState['accessoriesSize'] : undefined;
      const slotCandidate = sanitizeGenericText(String(candidate.slot ?? ''), 40);
      const slot = slotCandidate || (accessoriesSize ? 'accessory' : undefined);

      if (!selectionId || !title || !imageUrl) {
        return null;
      }

      return {
        selectionId,
        sourceType,
        cartItemId,
        itemId,
        inventoryIds,
        slot,
        title,
        imageUrl,
        price,
        accessoriesSize,
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  return sourceItems.length > 0 ? sourceItems : undefined;
}

export function getGeneratedTryOnResultState(value: unknown): GeneratedTryOnResultState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const imageUrl = sanitizeTryOnResultImageUrl(candidate.imageUrl ?? candidate.image_url);
  const objectKey = sanitizeGenericText(String(candidate.objectKey ?? candidate.object_key ?? ''), 512);
  const resultId = sanitizeGenericText(String(candidate.resultId ?? candidate.result_id ?? ''), 160) || undefined;
  const taskId = sanitizeGenericText(String(candidate.taskId ?? candidate.task_id ?? ''), 160) || undefined;
  const productName = sanitizeGenericText(String(candidate.productName ?? candidate.product_name ?? ''), 120);
  const productImageUrl = sanitizeImageUrl(String(candidate.productImageUrl ?? candidate.product_image_url ?? ''));
  const parsedPrice = typeof candidate.price === 'number' ? candidate.price : Number(candidate.price);
  const price = Number.isFinite(parsedPrice) && parsedPrice > 0 ? Number(parsedPrice.toFixed(2)) : null;
  const provider = sanitizeTryOnImageProvider(candidate.provider);
  const videoGenerationDisabled = candidate.videoGenerationDisabled === true;
  const aiDataId = sanitizeOptionalUuid(candidate.aiDataId ?? candidate.ai_data_id);
  const isSaved =
    candidate.isSaved === true ||
    candidate.is_saved === true ||
    String(candidate.isSaved ?? candidate.is_saved ?? '').toLowerCase() === 'true';
  const itemIds = sanitizeTryOnProductItemIds(
    candidate.itemIds ?? candidate.item_ids ?? candidate.itemId ?? candidate.item_id
  );
  const itemId = itemIds?.[0] ?? sanitizeTryOnProductItemId(candidate.itemId ?? candidate.item_id);
  const inventoryIds = sanitizeTryOnInventoryIds(candidate.inventoryIds ?? candidate.inventory_ids);
  const rawDataId = sanitizeOptionalUuid(candidate.rawDataId ?? candidate.raw_data_id);
  const sourceItems = sanitizeTryOnSourceItems(candidate.sourceItems ?? candidate.source_items);

  if ((!imageUrl && !objectKey) || !productName) {
    return null;
  }

  return {
    imageUrl,
    objectKey,
    resultId,
    taskId,
    productName,
    productImageUrl,
    price,
    provider,
    videoGenerationDisabled,
    aiDataId,
    isSaved,
    itemId,
    itemIds,
    inventoryIds,
    rawDataId,
    sourceItems,
  };
}

export function getPendingTryOnTaskState(value: unknown): PendingTryOnTaskState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const taskId = sanitizeGenericText(String(candidate.taskId ?? ''), 100);
  const productName = sanitizeGenericText(String(candidate.productName ?? ''), 120);
  const productImageUrl = sanitizeImageUrl(String(candidate.productImageUrl ?? ''));
  const startedAt = sanitizeGenericText(String(candidate.startedAt ?? ''), 80);
  const parsedPrice = typeof candidate.price === 'number' ? candidate.price : Number(candidate.price);
  const price = Number.isFinite(parsedPrice) && parsedPrice > 0 ? Number(parsedPrice.toFixed(2)) : null;
  const provider = sanitizeTryOnImageProvider(candidate.provider);
  const itemIds = sanitizeTryOnProductItemIds(
    candidate.itemIds ?? candidate.item_ids ?? candidate.itemId ?? candidate.item_id
  );
  const itemId = itemIds?.[0] ?? sanitizeTryOnProductItemId(candidate.itemId ?? candidate.item_id);
  const inventoryIds = sanitizeTryOnInventoryIds(candidate.inventoryIds ?? candidate.inventory_ids);
  const activeInventoryIds = sanitizeTryOnInventoryIds(
    candidate.activeInventoryIds ?? candidate.active_inventory_ids
  );
  const rawDataId = sanitizeOptionalUuid(candidate.rawDataId ?? candidate.raw_data_id);
  const sourceItems = sanitizeTryOnSourceItems(candidate.sourceItems ?? candidate.source_items);
  const addItemSelectionId = sanitizeGenericText(
    String(candidate.addItemSelectionId ?? candidate.add_item_selection_id ?? ''),
    120
  ) || undefined;

  if (!taskId || !productName || !startedAt) {
    return null;
  }

  return {
    taskId,
    productName,
    productImageUrl,
    price,
    startedAt,
    provider,
    itemId,
    itemIds,
    inventoryIds,
    activeInventoryIds,
    rawDataId,
    sourceItems,
    addItemSelectionId,
  };
}

function getPendingTryOnTaskQueueState(value: unknown): PersistedPendingTryOnTaskQueueState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const ownerKey = sanitizeGenericText(String(candidate.ownerKey ?? ''), 120);
  if (ownerKey !== getTryOnSessionOwnerKey()) {
    return null;
  }

  const rawTasks = Array.isArray(candidate.tasks) ? candidate.tasks : [];
  const tasks = dedupePendingTryOnTasks(
    rawTasks
      .map((task) => getPendingTryOnTaskState(task))
      .filter((task): task is PendingTryOnTaskState => task !== null)
  );

  return {
    ownerKey,
    tasks,
  };
}

export function getPendingTryOnVideoTaskState(value: unknown): PendingTryOnVideoTaskState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const taskId = sanitizeGenericText(String(candidate.taskId ?? ''), 160);
  const sourceImageUrl = sanitizeTryOnVideoSourceImageUrl(candidate.sourceImageUrl);
  const sourceImageObjectKey =
    sanitizeGenericText(
      String(candidate.sourceImageObjectKey ?? candidate.source_image_object_key ?? ''),
      512
    ) || undefined;
  const sourceImageAiDataId = sanitizeOptionalUuid(
    candidate.sourceImageAiDataId ?? candidate.source_image_ai_data_id
  );
  const startedAt = sanitizeGenericText(String(candidate.startedAt ?? ''), 80);
  const provider =
    candidate.provider === 'standard'
      ? 'standard'
      : candidate.provider === 'enhanced'
        ? 'enhanced'
        : 'standard';
  const model =
    candidate.model === 'standard-video'
      ? 'standard-video'
      : candidate.model === 'portrait-video'
        ? 'portrait-video'
        : 'motion-video';
  const prompt = sanitizeGenericText(String(candidate.prompt ?? ''), 600);
  const itemIds = sanitizeTryOnProductItemIds(
    candidate.itemIds ?? candidate.item_ids ?? candidate.itemId ?? candidate.item_id
  );
  const itemId = itemIds?.[0] ?? sanitizeTryOnProductItemId(candidate.itemId ?? candidate.item_id);
  const inventoryIds = sanitizeTryOnInventoryIds(candidate.inventoryIds ?? candidate.inventory_ids);
  const rawDataId = sanitizeOptionalUuid(candidate.rawDataId ?? candidate.raw_data_id);

  if (!taskId || !startedAt) {
    return null;
  }

  return {
    taskId,
    sourceImageUrl,
    sourceImageObjectKey,
    sourceImageAiDataId,
    startedAt,
    provider,
    model,
    prompt,
    itemId,
    itemIds,
    inventoryIds,
    rawDataId,
  };
}

export function getGeneratedTryOnVideoResultState(value: unknown): GeneratedTryOnVideoResultState | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const videoUrl = sanitizeImageUrl(candidate.videoUrl, true);
  const sourceVideoUrl = sanitizeRemoteAssetUrl(
    candidate.sourceVideoUrl ?? candidate.source_video_url
  );
  const sourceImageUrl = sanitizeTryOnVideoSourceImageUrl(candidate.sourceImageUrl);
  const sourceImageObjectKey =
    sanitizeGenericText(
      String(candidate.sourceImageObjectKey ?? candidate.source_image_object_key ?? ''),
      512
    ) || undefined;
  const sourceImageAiDataId = sanitizeOptionalUuid(
    candidate.sourceImageAiDataId ?? candidate.source_image_ai_data_id
  );
  const objectKey = sanitizeGenericText(String(candidate.objectKey ?? ''), 512);
  const videoId = sanitizeGenericText(String(candidate.videoId ?? candidate.video_id ?? ''), 300);
  const taskId = sanitizeGenericText(String(candidate.taskId ?? ''), 160);
  const isSaved =
    candidate.isSaved === true
    || candidate.is_saved === true
    || String(candidate.isSaved ?? candidate.is_saved ?? '').toLowerCase() === 'true';
  const provider =
    candidate.provider === 'standard'
      ? 'standard'
      : candidate.provider === 'enhanced'
        ? 'enhanced'
        : 'standard';
  const model =
    candidate.model === 'standard-video'
      ? 'standard-video'
      : candidate.model === 'portrait-video'
        ? 'portrait-video'
        : 'motion-video';
  const prompt = sanitizeGenericText(String(candidate.prompt ?? ''), 600);
  const itemIds = sanitizeTryOnProductItemIds(
    candidate.itemIds ?? candidate.item_ids ?? candidate.itemId ?? candidate.item_id
  );
  const itemId = itemIds?.[0] ?? sanitizeTryOnProductItemId(candidate.itemId ?? candidate.item_id);
  const inventoryIds = sanitizeTryOnInventoryIds(candidate.inventoryIds ?? candidate.inventory_ids);
  const rawDataId = sanitizeOptionalUuid(candidate.rawDataId ?? candidate.raw_data_id);

  if (!videoUrl && !sourceVideoUrl && !objectKey) {
    return null;
  }

  return {
    videoUrl: sourceVideoUrl || videoUrl,
    videoId: videoId || undefined,
    isSaved,
    sourceVideoUrl: sourceVideoUrl || undefined,
    sourceImageUrl,
    sourceImageObjectKey,
    sourceImageAiDataId,
    objectKey: objectKey || undefined,
    taskId: taskId || undefined,
    provider,
    model,
    prompt: prompt || undefined,
    itemId,
    itemIds,
    inventoryIds,
    rawDataId,
  };
}

export function readPersistedTryOnSession() {
  const rawValue = readRawPersistedTryOnSessionValue();
  if (!rawValue) {
    return null;
  }

  const parsedValue = safeJsonParse<Record<string, unknown> | null>(rawValue, null);
  if (!parsedValue || typeof parsedValue !== 'object') {
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'local' });
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
    return null;
  }

  const ownerKey = sanitizeGenericText(String(parsedValue.ownerKey ?? ''), 120);
  if (ownerKey !== getTryOnSessionOwnerKey()) {
    return null;
  }

  const persistedSession = {
    ownerKey,
    shouldSaveAssetsToLibrary: getShouldSaveAssetsToLibraryState(
      parsedValue.shouldSaveAssetsToLibrary
    ),
    uploadedTryOnPhoto: getUploadedTryOnPhotoState(parsedValue.uploadedTryOnPhoto),
    generatedTryOnResult: getGeneratedTryOnResultState(parsedValue.generatedTryOnResult),
    pendingTryOnTask: getPendingTryOnTaskState(parsedValue.pendingTryOnTask),
    pendingTryOnVideoTask: getPendingTryOnVideoTaskState(parsedValue.pendingTryOnVideoTask),
    generatedTryOnVideoResult: getGeneratedTryOnVideoResultState(parsedValue.generatedTryOnVideoResult),
  };

  if (hasPersistedTryOnSession(persistedSession)) {
    writeStorageValue(
      ACTIVE_TRY_ON_SESSION_STORAGE_KEY,
      JSON.stringify(persistedSession),
      { storage: 'local' }
    );
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
    return persistedSession;
  }

  return null;
}

export function readQueuedPendingTryOnTasks() {
  const rawValue = readRawPendingTryOnTaskQueueValue();
  if (!rawValue) {
    return [];
  }

  const parsedValue = safeJsonParse<Record<string, unknown> | null>(rawValue, null);
  const queueState = getPendingTryOnTaskQueueState(parsedValue);
  if (!queueState) {
    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'local' });
    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'session' });
    return [];
  }

  const serializedQueue = JSON.stringify(queueState);
  if (serializedQueue !== rawValue) {
    writeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, serializedQueue, { storage: 'local' });
    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'session' });
  }

  return queueState.tasks;
}

function persistQueuedPendingTryOnTasks(tasks: PendingTryOnTaskState[]) {
  const existingRawValue = readRawPendingTryOnTaskQueueValue();
  const nextTasks = dedupePendingTryOnTasks(tasks);

  if (nextTasks.length <= 0) {
    if (!existingRawValue) {
      return;
    }

    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'local' });
    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'session' });
    dispatchTryOnSessionChange();
    return;
  }

  const nextQueueState: PersistedPendingTryOnTaskQueueState = {
    ownerKey: getTryOnSessionOwnerKey(),
    tasks: nextTasks,
  };
  const serializedQueue = JSON.stringify(nextQueueState);

  if (existingRawValue === serializedQueue) {
    return;
  }

  try {
    writeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, serializedQueue, { storage: 'local' });
    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'session' });
    dispatchTryOnSessionChange();
  } catch {
    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'local' });
    removeStorageValue(PENDING_TRY_ON_TASK_QUEUE_STORAGE_KEY, { storage: 'session' });
    dispatchTryOnSessionChange();
  }
}

export function enqueuePendingTryOnTask(task: PendingTryOnTaskState | null | undefined) {
  const nextTask = getPendingTryOnTaskState(task);
  if (!nextTask) {
    return;
  }

  persistQueuedPendingTryOnTasks([...readQueuedPendingTryOnTasks(), nextTask]);
}

export function removeQueuedPendingTryOnTask(
  taskId: string | undefined,
  provider?: TryOnImageProvider
) {
  const sanitizedTaskId = sanitizeGenericText(String(taskId ?? ''), 160);
  if (!sanitizedTaskId) {
    return;
  }

  const nextTasks = readQueuedPendingTryOnTasks().filter((task) => {
    if (task.taskId !== sanitizedTaskId) {
      return true;
    }

    return provider ? task.provider !== provider : false;
  });

  persistQueuedPendingTryOnTasks(nextTasks);
}

export function persistTryOnSession(
  value: {
      shouldSaveAssetsToLibrary?: boolean;
      uploadedTryOnPhoto?: UploadedTryOnPhotoState | null;
      generatedTryOnResult?: GeneratedTryOnResultState | null;
      pendingTryOnTask?: PendingTryOnTaskState | null;
      pendingTryOnVideoTask?: PendingTryOnVideoTaskState | null;
      generatedTryOnVideoResult?: GeneratedTryOnVideoResultState | null;
    } | null
) {
  const existingRawValue = readRawPersistedTryOnSessionValue();
  const existingPersistedSession = existingRawValue ? readPersistedTryOnSession() : null;

  if (!value) {
    if (!existingRawValue) {
      return;
    }

    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'local' });
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
    dispatchTryOnSessionChange();
    return;
  }

  const persistedSession: PersistedTryOnSessionState = {
    ownerKey: getTryOnSessionOwnerKey(),
    shouldSaveAssetsToLibrary:
      value.shouldSaveAssetsToLibrary ??
      existingPersistedSession?.shouldSaveAssetsToLibrary ??
      true,
    uploadedTryOnPhoto: value.uploadedTryOnPhoto ?? null,
    generatedTryOnResult: value.generatedTryOnResult ?? null,
    pendingTryOnTask: value.pendingTryOnTask ?? null,
    pendingTryOnVideoTask: value.pendingTryOnVideoTask ?? null,
    generatedTryOnVideoResult: value.generatedTryOnVideoResult ?? null,
  };

  if (
    persistedSession.generatedTryOnResult &&
    sanitizeDataImageUrl(persistedSession.generatedTryOnResult.imageUrl) &&
    !persistedSession.generatedTryOnResult.objectKey &&
    persistedSession.generatedTryOnResult.provider !== 'standard'
  ) {
    persistedSession.generatedTryOnResult = null;
    if (persistedSession.pendingTryOnTask?.provider === 'detailed') {
      persistedSession.pendingTryOnTask = null;
    }
  }

  if (
    persistedSession.generatedTryOnResult &&
    persistedSession.pendingTryOnTask?.provider === 'standard' &&
    persistedSession.pendingTryOnTask.taskId &&
    isRemoteGeneratedMediaUrl(persistedSession.generatedTryOnResult.imageUrl)
  ) {
    persistedSession.generatedTryOnResult = {
      ...persistedSession.generatedTryOnResult,
      imageUrl: buildTryOnTaskImageEndpoint(persistedSession.pendingTryOnTask.taskId, 'standard'),
    };
  }

  if (!hasPersistedTryOnSession(persistedSession)) {
    if (!existingRawValue) {
      return;
    }

    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'local' });
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
    dispatchTryOnSessionChange();
    return;
  }

  const serializedSession = JSON.stringify(persistedSession);
  if (existingRawValue === serializedSession) {
    return;
  }

  try {
    writeStorageValue(
      ACTIVE_TRY_ON_SESSION_STORAGE_KEY,
      serializedSession,
      { storage: 'local' }
    );
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
    dispatchTryOnSessionChange();
  } catch {
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'local' });
    removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
    dispatchTryOnSessionChange();
  }
}

export function persistTryOnVideoProgress(
  pendingTryOnVideoTask: PendingTryOnVideoTaskState | null,
  generatedTryOnVideoResult: GeneratedTryOnVideoResultState | null
) {
  const currentSession = readPersistedTryOnSession();
  const persistablePendingVideoTask =
    pendingTryOnVideoTask?.sourceImageObjectKey &&
    pendingTryOnVideoTask.sourceImageUrl.startsWith('blob:')
      ? {
          ...pendingTryOnVideoTask,
          sourceImageUrl: '',
        }
      : pendingTryOnVideoTask;
  const persistableGeneratedVideoResult =
    generatedTryOnVideoResult?.objectKey &&
    generatedTryOnVideoResult.videoUrl.startsWith('blob:') &&
    !generatedTryOnVideoResult.sourceVideoUrl
      ? {
          ...generatedTryOnVideoResult,
          videoUrl: '',
        }
      : generatedTryOnVideoResult;
  const persistableGeneratedVideoResultWithSource =
    persistableGeneratedVideoResult?.sourceImageObjectKey &&
    persistableGeneratedVideoResult.sourceImageUrl.startsWith('blob:')
      ? {
          ...persistableGeneratedVideoResult,
          sourceImageUrl: '',
        }
      : persistableGeneratedVideoResult;

  persistTryOnSession({
    shouldSaveAssetsToLibrary: currentSession?.shouldSaveAssetsToLibrary ?? true,
    uploadedTryOnPhoto: currentSession?.uploadedTryOnPhoto ?? null,
    generatedTryOnResult: currentSession?.generatedTryOnResult ?? null,
    pendingTryOnTask: currentSession?.pendingTryOnTask ?? null,
    pendingTryOnVideoTask: persistablePendingVideoTask,
    generatedTryOnVideoResult: persistableGeneratedVideoResultWithSource,
  });
}

export function clearPersistedTryOnSession() {
  removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'local' });
  removeStorageValue(ACTIVE_TRY_ON_SESSION_STORAGE_KEY, { storage: 'session' });
  dispatchTryOnSessionChange();
}
